/* import __COLOCATED_TEMPLATE__ from './team-details-row.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type InboxState from 'embercom/services/inbox-state';
import type AdminSummary from 'embercom/objects/inbox/admin-summary';
import type Session from 'embercom/services/session';
import type TeamSummary from 'embercom/objects/inbox/team-summary';

const INBOX_PRIORITY_PRIMARY = 1;
const COUNT_CLASS_NAMES = {
  atCapacity: 'text-error ',
  notAtCapacity: 'text-muted ',
};

type CountType = 'all' | 'conversations' | 'tickets';

interface Args {
  admin: AdminSummary;
  team: TeamSummary;
  capacityLimit: number;
  adminsBlockable: boolean;
  countType: CountType;
  ticketsAssignmentDisabled: boolean;
  ticketAssignmentLimit: number;
  ticketAssignmentLimitEnabled: boolean;
}

interface Signature {
  Args: Args;
  Element: HTMLDivElement;
}

export default class UnnamedComponent extends Component<Signature> {
  @service declare inboxState: InboxState;
  @service declare session: Session;

  get adminAssignmentLimit(): number | undefined {
    return this.args.admin.workloadManagementSettings?.assignmentLimit;
  }

  get assignmentLimit(): number {
    return this.adminAssignmentLimit ?? this.args.capacityLimit;
  }

  get adminTicketAssignmentLimit(): number | undefined {
    return this.args.admin.workloadManagementSettings?.ticketAssignmentLimit;
  }

  get ticketAssignmentLimit(): number {
    return this.adminTicketAssignmentLimit ?? this.args.ticketAssignmentLimit;
  }

  get aboveCapacity() {
    return this.adminInboxCount! >= this.adminCountTypeLimit;
  }

  get isPrimaryInbox() {
    return this.args.admin.wlmInboxPriorityLevel === INBOX_PRIORITY_PRIMARY;
  }

  get separateCapacityLimitEnabled(): boolean {
    return !this.args.ticketsAssignmentDisabled && this.args.ticketAssignmentLimitEnabled;
  }

  get adminCountTypeLimit(): number {
    let limitMap: Record<CountType, number> = {
      tickets: this.ticketAssignmentLimit,
      conversations: this.assignmentLimit,
      all: this.assignmentLimit,
    };
    return limitMap[this.args.countType] ?? this.assignmentLimit;
  }

  get adminInboxCount(): number | undefined {
    let countMap: Record<CountType, number | undefined> = {
      tickets: this.adminTicketsCount,
      conversations: this.adminConversationsCount,
      all: this.args.admin.openCount,
    };
    return countMap[this.args.countType];
  }

  get shouldShowAtCapacity(): boolean {
    if (!this.args.adminsBlockable || !this.aboveCapacity) {
      return false;
    }

    if (this.separateCapacityLimitEnabled) {
      return this.shouldShowCapacityForSeparateLimits();
    }

    return this.shouldShowCapacityForLegacyMode();
  }

  private shouldShowCapacityForSeparateLimits(): boolean {
    let capacityChecks: Record<CountType, boolean> = {
      all: this.isAtBothLimits(),
      tickets: this.isAtTicketLimit(),
      conversations: this.isAtConversationLimit(),
    };
    return capacityChecks[this.args.countType] ?? false;
  }

  private shouldShowCapacityForLegacyMode(): boolean {
    let showConditions: Record<CountType, boolean> = {
      all: !this.args.ticketsAssignmentDisabled,
      conversations: this.args.ticketsAssignmentDisabled,
      tickets: false,
    };
    return showConditions[this.args.countType] ?? false;
  }

  private isAtBothLimits(): boolean {
    return this.isAtTicketLimit() && this.isAtConversationLimit();
  }

  private isAtTicketLimit(): boolean {
    return this.adminTicketsCount >= this.ticketAssignmentLimit;
  }

  private isAtConversationLimit(): boolean {
    return this.adminConversationsCount >= this.assignmentLimit;
  }

  get countClassNames() {
    if (this.shouldShowAtCapacity) {
      return COUNT_CLASS_NAMES.atCapacity;
    }

    return COUNT_CLASS_NAMES.notAtCapacity;
  }

  get adminConversationsCount(): number {
    return this.args.admin.countMetadata?.conversations ?? 0;
  }

  get adminTicketsCount(): number {
    return this.args.admin.countMetadata?.tickets ?? 0;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::TeamDetailsRow': typeof UnnamedComponent;
    'inbox2/team-details-row': typeof UnnamedComponent;
  }
}
